import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  useRef,
} from 'react'
import { Form } from 'antd'
import { debounce } from 'lodash'
import { getDataOfType } from 'src/lib/utils/get-data-of-type'
import ContractOrderPricePay from 'src/react-app/connectors/contract-order-price-pay'
import ContractDebt from 'src/react-app/connectors/contract-debt'
import { InputPaymentAmount } from '../../input-payment-amount/ui/input-payment-amount'
import { FormItem, FormWrapper, GroupBody } from './styles'
import { FieldGroup } from '../field-group'
import './index.css'
import { getInputNodeCustom } from './field-renderers'
import { getFiledRules, getUISchemaValue } from './form-utils'

function FormDataEditor(
  {
    formData = {},
    schema,
    UISchema,
    onChange,
    orientation = 'vertical',
  }: {
    formData
    schema
    UISchema?
    onChange
    orientation?
  },
  ref,
) {
  const [form] = Form.useForm()
  const [required, setRequired] = useState<string[]>(schema?.required || [])
  const containerRef = useRef<HTMLDivElement>(null)
  const [isInModal, setIsInModal] = useState(false)

  useImperativeHandle(ref, () => ({
    validateFields: params => form.validateFields(params),
  }))

  useEffect(() => {
    if (containerRef.current) {
      const modalElement = containerRef.current.closest(
        '.ant-modal, .ant-modal-content, [role="dialog"]',
      )
      setIsInModal(!!modalElement)
    }
  }, [])

  useEffect(() => {
    const antFromData = form.getFieldsValue()
    const formDataKeys = Object.keys(formData || {})
    const nextAntFormData = {}

    if (schema?.required) {
      setRequired(
        schema.required.filter((key: string) => !formData[key] || formData[key].length === 0),
      )
    }

    formDataKeys.forEach(key => {
      if (Array.isArray(formData[key])) {
        nextAntFormData[key] = formData[key]
      } else if (typeof formData[key] === 'object') {
        return
      } else if (!(formData[key] === antFromData[key])) {
        nextAntFormData[key] = formData[key]
      }
    })
    form.setFieldsValue(nextAntFormData)
    return () => {}
  }, [form, formData])

  const onValuesChange = useCallback(
    debounce((_, nextFormData = {}) => {
      const mergedFormData = Object.assign({}, formData ?? {}, nextFormData)
      onChange({ formData: mergedFormData, schema })
    }, 200),
    [schema, onChange, formData],
  )

  const properties: any = useMemo(() => {
    const propertiesSchema = schema['properties'] || {}
    const requiredSchema: any[] = schema['required'] || []

    const properties = Object.entries(propertiesSchema).map(([key, value]: [string, any]) => {
      const nextValue = Object.assign({ key }, value)
      const UISchemaValue = getUISchemaValue(UISchema, key)
      if (UISchemaValue?.required) {
        UISchemaValue.required = required.includes(key)
      }
      const InputNode: any = getInputNodeCustom(
        nextValue,
        UISchemaValue,
        formData?.[nextValue?.['key']],
      )
      return {
        ...value,
        title: value['title'] || key,
        name: key,
        render: InputNode,
        required: requiredSchema.includes(key),
      }
    })

    return properties.filter(({ render }) => render !== null)
  }, [schema, formData, UISchema])

  useEffect(() => {
    form.validateFields()
  }, [UISchema])

  const renderGroupedFields = () => {
    if (!schema.groups || Object.keys(schema.groups).length === 0) {
      return renderFlatFields()
    }

    const mergedFieldsMap = new Map()

    properties.forEach(({ name, merge_fields }) => {
      if (merge_fields) {
        merge_fields.forEach(field => {
          mergedFieldsMap.set(field, name)
        })
      }
    })

    const groups = Object.entries(schema.groups).sort(
      ([, a], [, b]) => (a as any).sort_order - (b as any).sort_order,
    )

    return groups.map(([groupId, group], index) => {
      const groupData = group as any
      const groupFields = properties.filter(
        ({ name }) =>
          groupData.fields.includes(name) &&
          getDataOfType(UISchema, [name, 'hidden'], Boolean, false) === false &&
          !mergedFieldsMap.has(name),
      )

      if (groupFields.length === 0) return null

      return (
        <FieldGroup key={groupId} title={groupData.title} isFirst={index === 0}>
          <GroupBody>
            {groupFields.map(({ title, name, render, merge_fields, ...props }) =>
              renderFieldItem({ title, name, render, merge_fields, ...props }),
            )}
          </GroupBody>
        </FieldGroup>
      )
    })
  }

  const renderFlatFields = () => {
    const mergedFieldsMap = new Map()

    properties.forEach(({ name, merge_fields }) => {
      if (merge_fields) {
        merge_fields.forEach(field => {
          mergedFieldsMap.set(field, name)
        })
      }
    })

    return properties
      .filter(({ name }) => getDataOfType(UISchema, [name, 'hidden'], Boolean, false) === false)
      .map(({ title, name, render, merge_fields, ...props }) => {
        if (mergedFieldsMap.has(name)) {
          return null
        }
        return renderFieldItem({ title, name, render, merge_fields, ...props })
      })
  }

  const renderFieldItem = ({ title, name, render, merge_fields, ...props }) => {
    const requiredField =
      (props?.required || props?.options?.required) &&
      (!formData?.[name] || formData?.[name]?.length === 0)
    const rules = getFiledRules(props, requiredField)
    const style = props?.style

    const additionalFields =
      merge_fields
        ?.map(fieldName => properties.find(prop => prop.name === fieldName))
        .filter(Boolean) || []

    return (
      <div
        key={name}
        data-field-name={name}
        style={{ ...style, background: 'none', width: '100%' }}
      >
        {name === 'order-price' ? (
          props.view === 'new' ? (
            <FormItem>
              <InputPaymentAmount
                name={name}
                title={title}
                render={render}
                UISchema={UISchema}
                otherProps={props}
                rules={rules}
                required={requiredField}
              />
            </FormItem>
          ) : (
            <FormItem>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Form.Item label={title} name={name}>
                  {render}
                </Form.Item>
                <Form.Item label="Задолженность" name={name}>
                  <ContractDebt />
                </Form.Item>
                <Form.Item label="Предоплата" name="contract-order-price-pay">
                  <ContractOrderPricePay UISchema={UISchema} />
                </Form.Item>
              </div>
            </FormItem>
          )
        ) : (
          <FormItem $mergedFields={additionalFields.length > 0}>
            <Form.Item
              style={{ width: '100%' }}
              label={props?.type !== 'devider' && props?.type !== 'boolean' ? title : null}
              name={name}
              rules={rules}
              required={requiredField}
            >
              {render}
            </Form.Item>

            {additionalFields.map(field => (
              <Form.Item
                style={{ width: '100%' }}
                key={field.name}
                label={field?.options?.hide_title ? '' : field.title}
                name={field.name}
                rules={getFiledRules(field, field.required)}
                required={field.required}
                className={field?.options?.highlight ? 'highlight' : ''}
              >
                {field.render}
              </Form.Item>
            ))}
          </FormItem>
        )}
      </div>
    )
  }

  return (
    <div ref={containerRef} className="json-form-object-editor">
      <Form
        component="div"
        layout="vertical"
        form={form}
        onValuesChange={onValuesChange}
        initialValues={formData}
      >
        <FormWrapper orientation={orientation} isInModal={isInModal}>
          {renderGroupedFields()}
        </FormWrapper>
      </Form>
    </div>
  )
}

export default forwardRef(FormDataEditor)
